import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useUserSession } from '@/contexts/UserSessionContext';
import { retroalimentacionesService } from '@/services/api/retroalimentaciones.service';
import { ContextoRetroalimentacion } from './ContextoRetroalimentacion';
import {
  MAXIMO_COMENTARIO_BREVE_RETROALIMENTACION,
  MAXIMO_IDEA_RETROALIMENTACION,
  PUNTUACION_NPS_MAXIMA,
  PUNTUACION_NPS_MINIMA,
} from './constantes';
import { esPuntuacionNpsValida, normalizarTextoBreve } from './reglasFrecuencia';
import type { EnvioEstadoAnimo, EnvioIdea, EnvioRespuestaNps, FlujoRetroalimentacion, OrigenAperturaRetroalimentacion, RetroalimentacionApi } from './tipos';

const FLUJO_INICIAL: FlujoRetroalimentacion = 'estado_animo';

function resolverModuloDesdeRuta(pathname: string): string {
  const primerSegmento = pathname
    .split('/')
    .map((segmento) => segmento.trim())
    .filter(Boolean)[0];

  return primerSegmento?.toLowerCase() ?? 'inicio';
}

function resolverRutaActual(pathname: string, search: string): string {
  return `${pathname}${search ?? ''}` || '/';
}

function validarComentarioBreve(comentario: string | undefined): boolean {
  return normalizarTextoBreve(comentario).length <= MAXIMO_COMENTARIO_BREVE_RETROALIMENTACION;
}

function validarIdea(contenido: string): boolean {
  const texto = normalizarTextoBreve(contenido);
  return texto.length > 0 && texto.length <= MAXIMO_IDEA_RETROALIMENTACION;
}

function validarRespuestaNps(entrada: EnvioRespuestaNps): boolean {
  return esPuntuacionNpsValida(entrada.puntuacion)
    && validarComentarioBreve(entrada.comentario)
    && entrada.puntuacion >= PUNTUACION_NPS_MINIMA
    && entrada.puntuacion <= PUNTUACION_NPS_MAXIMA;
}

export function ProveedorRetroalimentacion({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { session } = useUserSession();
  const [panelAbierto, setPanelAbierto] = useState(false);
  const [flujoActivo, setFlujoActivo] = useState<FlujoRetroalimentacion>(FLUJO_INICIAL);
  const [ultimaRespuestaNpsEn, setUltimaRespuestaNpsEn] = useState<string | null>(null);
  const [contadorEnvios, setContadorEnvios] = useState(0);
  const registrarEnvioExitoso = useCallback(() => setContadorEnvios((actual) => actual + 1), []);

  const contextoRegistro = useMemo(() => {
    if (!session || !session.currentCompanyId) {
      return null;
    }

    return {
      empresaId: session.currentCompanyId,
      establecimientoId: session.currentEstablecimientoId ?? undefined,
      modulo: resolverModuloDesdeRuta(location.pathname),
      ruta: resolverRutaActual(location.pathname, location.search),
    };
  }, [location.pathname, location.search, session]);

  const abrirPanel = useCallback((
    flujo: FlujoRetroalimentacion = FLUJO_INICIAL,
    _origen: OrigenAperturaRetroalimentacion = 'header',
  ) => {
    setFlujoActivo(flujo);
    setPanelAbierto(true);
  }, []);

  const cerrarPanel = useCallback(() => {
    setPanelAbierto(false);
  }, []);

  const cambiarFlujo = useCallback((flujo: FlujoRetroalimentacion) => {
    setFlujoActivo(flujo);
    setPanelAbierto(true);
  }, []);

  const solicitarEncuestaNps = useCallback(() => {
    abrirPanel('nps', 'automatico');
  }, [abrirPanel]);

  const enviarEstadoAnimo = useCallback(async (entrada: EnvioEstadoAnimo): Promise<boolean> => {
    const comentario = normalizarTextoBreve(entrada.comentario);

    if (!entrada.estado || !validarComentarioBreve(comentario) || !contextoRegistro) {
      return false;
    }

    try {
      await retroalimentacionesService.create({
        empresaId: contextoRegistro.empresaId,
        establecimientoId: contextoRegistro.establecimientoId,
        tipo: 'ESTADO_ANIMO',
        estadoAnimo: entrada.estado,
        comentario: comentario || undefined,
        modulo: contextoRegistro.modulo,
        ruta: contextoRegistro.ruta,
      });
      registrarEnvioExitoso();
      return true;
    } catch {
      return false;
    }
  }, [contextoRegistro, registrarEnvioExitoso]);

  const enviarIdea = useCallback(async (entrada: EnvioIdea): Promise<boolean> => {
    const contenido = normalizarTextoBreve(entrada.contenido);

    if (!validarIdea(contenido) || !contextoRegistro) {
      return false;
    }

    try {
      await retroalimentacionesService.create({
        empresaId: contextoRegistro.empresaId,
        establecimientoId: contextoRegistro.establecimientoId,
        tipo: 'IDEA',
        ideaContenido: contenido,
        modulo: contextoRegistro.modulo,
        ruta: contextoRegistro.ruta,
      });
      registrarEnvioExitoso();
      return true;
    } catch {
      return false;
    }
  }, [contextoRegistro, registrarEnvioExitoso]);

  const enviarRespuestaNps = useCallback(async (entrada: EnvioRespuestaNps): Promise<boolean> => {
    const respuesta = {
      ...entrada,
      comentario: normalizarTextoBreve(entrada.comentario),
    };

    if (!validarRespuestaNps(respuesta) || !contextoRegistro) {
      return false;
    }

    try {
      await retroalimentacionesService.create({
        empresaId: contextoRegistro.empresaId,
        establecimientoId: contextoRegistro.establecimientoId,
        tipo: 'NPS',
        npsPuntuacion: respuesta.puntuacion,
        comentario: respuesta.comentario || undefined,
        modulo: contextoRegistro.modulo,
        ruta: contextoRegistro.ruta,
      });
      setUltimaRespuestaNpsEn(new Date().toISOString());
      registrarEnvioExitoso();
      return true;
    } catch {
      return false;
    }
  }, [contextoRegistro, registrarEnvioExitoso]);

  const value = useMemo<RetroalimentacionApi>(() => ({
    panelAbierto,
    flujoActivo,
    ultimaRespuestaNpsEn,
    contadorEnvios,
    abrirPanel,
    cerrarPanel,
    cambiarFlujo,
    solicitarEncuestaNps,
    enviarEstadoAnimo,
    enviarIdea,
    enviarRespuestaNps,
  }), [
    panelAbierto,
    flujoActivo,
    ultimaRespuestaNpsEn,
    contadorEnvios,
    abrirPanel,
    cerrarPanel,
    cambiarFlujo,
    solicitarEncuestaNps,
    enviarEstadoAnimo,
    enviarIdea,
    enviarRespuestaNps,
  ]);

  return <ContextoRetroalimentacion.Provider value={value}>{children}</ContextoRetroalimentacion.Provider>;
}
